import React, { useState, useRef, useEffect } from 'react';
import { fmtTokens } from '../utils/helpers';
import { calculateNetWorth } from '../constants/richness';
import './shell.css';

/* SVG icons — match Sidebar */
const sv = { fill: 'none', stroke: 'currentColor', strokeWidth: '1.8', strokeLinecap: 'round', strokeLinejoin: 'round' };
const IcoLeaderboard = () => <svg width="20" height="20" viewBox="0 0 24 24" {...sv}><path d="M18 20V10M12 20V4M6 20v-6" /></svg>;
const IcoBox         = () => <svg width="20" height="20" viewBox="0 0 24 24" {...sv}><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" /><path d="m3.3 7 8.7 5 8.7-5M12 22V12" /></svg>;
const IcoMarket      = () => <svg width="20" height="20" viewBox="0 0 24 24" {...sv}><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" /><line x1="3" x2="21" y1="6" y2="6" /><path d="M16 10a4 4 0 0 1-8 0" /></svg>;
const IcoCasino      = () => <svg width="20" height="20" viewBox="0 0 24 24" {...sv}><rect width="20" height="14" x="2" y="5" rx="2" /><line x1="2" x2="22" y1="10" y2="10" /></svg>;
const IcoStats       = () => <svg width="20" height="20" viewBox="0 0 24 24" {...sv}><line x1="18" x2="18" y1="20" y2="10" /><line x1="12" x2="12" y1="20" y2="4" /><line x1="6" x2="6" y1="20" y2="14" /></svg>;
const IcoPass        = () => <svg width="20" height="20" viewBox="0 0 24 24" {...sv}><path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v2z" /></svg>;
const IcoBell        = () => <svg width="15" height="15" viewBox="0 0 24 24" {...sv}><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" /></svg>;
const IcoGear        = () => <svg width="16" height="16" viewBox="0 0 24 24" {...sv}><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" /><circle cx="12" cy="12" r="3" /></svg>;

const BOTTOM_TABS = [
  { id: 'leaderboard', label: 'Ranks',  Icon: IcoLeaderboard },
  { id: 'lootbox',     label: 'Boxes',  Icon: IcoBox },
  { id: 'marketplace', label: 'Market', Icon: IcoMarket },
  { id: 'casino',      label: 'Casino', Icon: IcoCasino },
  { id: 'stats',       label: 'Stats',  Icon: IcoStats },
  { id: 'pass',        label: 'Pass',   Icon: IcoPass },
];

function formatTimeAgo(timestamp) {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export default function MobileNav({
  page, setPage, isAdmin, user, userData, tokenAdjustment,
  onLogout, onChangeIgn,
  unreadCount, notifications, showNotifications, setShowNotifications,
  onMarkRead, onClearAll, seasonConfig, hasClaimablePassRewards, liveStockValue,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = e => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const crewTokens = Math.max(0, (userData?.crewmateTokens || 0) - (tokenAdjustment?.crewmate || 0));
  const impTokens  = Math.max(0, (userData?.imposterTokens  || 0) - (tokenAdjustment?.imposter  || 0));
  const netWorth = userData
    ? calculateNetWorth(liveStockValue != null ? { ...userData, stockPortfolioValue: liveStockValue } : userData, true).total
    : 0;

  return (
    <>
      <div className="v2-mobile-top">
        <div className="v2-mobile-top__left">
          <img src="/logo192.png" alt="AmongLR" className="v2-mobile-top__logo" />
          <span className="v2-mobile-top__txt">AMONG<span>LR</span></span>
        </div>
        <div className="v2-mobile-top__right">
          {user && userData && (
            <div className="v2-mobile-currency">
              <span className="v2-mobile-pill v2-mobile-pill--crew">🔵 {fmtTokens(crewTokens)}</span>
              <span className="v2-mobile-pill v2-mobile-pill--imp">🔴 {fmtTokens(impTokens)}</span>
            </div>
          )}
          {user && (
            <button
              type="button"
              className="v2-mobile-icon-btn"
              onClick={() => setShowNotifications(!showNotifications)}
              aria-label="Notifications"
            >
              <IcoBell />
              {unreadCount > 0 && <span className="v2-mobile-icon-btn__badge">{unreadCount}</span>}
            </button>
          )}
          <div ref={menuRef} style={{ position: 'relative' }}>
            <button
              type="button"
              className="v2-mobile-icon-btn"
              onClick={() => setMenuOpen(o => !o)}
              aria-label="Menu"
            >
              <IcoGear />
            </button>
            {menuOpen && (
              <div className="v2-mobile-menu">
                {user && userData && (
                  <div style={{ padding: '6px 12px 10px', borderBottom: '1px solid var(--border)', marginBottom: 4 }}>
                    <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 11, fontWeight: 700, color: 'var(--green)' }}>${fmtTokens(netWorth)}</div>
                    <div style={{ fontSize: 10, color: 'var(--text-2)', marginTop: 2 }}>Net worth</div>
                  </div>
                )}
                <button
                  type="button"
                  className={`v2-mobile-menu__item${page === 'stocks' ? ' v2-mobile-menu__item--active' : ''}`}
                  onClick={() => { setPage('stocks'); setMenuOpen(false); }}
                >
                  📈 Stocks
                </button>
                <button
                  type="button"
                  className={`v2-mobile-menu__item${page === 'settings' ? ' v2-mobile-menu__item--active' : ''}`}
                  onClick={() => { setPage('settings'); setMenuOpen(false); }}
                >
                  ⚙️ Settings
                </button>
                {isAdmin && (
                  <button
                    type="button"
                    className={`v2-mobile-menu__item${page === 'admin' ? ' v2-mobile-menu__item--active' : ''}`}
                    onClick={() => { setPage('admin'); setMenuOpen(false); }}
                  >
                    👤 Players
                  </button>
                )}
                {user ? (
                  <>
                    <button type="button" className="v2-mobile-menu__item" onClick={() => { onChangeIgn(); setMenuOpen(false); }}>
                      ✏️ Change IGN
                    </button>
                    <button type="button" className="v2-mobile-menu__item v2-mobile-menu__item--danger" onClick={() => { onLogout(); setMenuOpen(false); }}>
                      🚪 Logout
                    </button>
                  </>
                ) : (
                  <button type="button" className="v2-mobile-menu__item" onClick={() => { setPage('admin'); setMenuOpen(false); }}>
                    🔓 Login
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Notification panel — reuses existing mobile-notif-* classes from Mobile.css */}
      {showNotifications && (
        <div className="mobile-notif-overlay" onClick={() => setShowNotifications(false)}>
          <div className="mobile-notif-panel" onClick={e => e.stopPropagation()}>
            <div className="mobile-notif-header">
              <h3>Notifications</h3>
              <div className="mobile-notif-actions">
                {notifications.length > 0 && (
                  <button onClick={onClearAll} className="mobile-notif-clear">Clear All</button>
                )}
                <button onClick={() => setShowNotifications(false)} className="mobile-notif-close">✕</button>
              </div>
            </div>
            <div className="mobile-notif-list">
              {notifications.length === 0 ? (
                <div className="mobile-notif-empty">No notifications</div>
              ) : (
                notifications.map(notif => (
                  <div
                    key={notif.id}
                    className={`mobile-notif-item ${notif.read ? '' : 'unread'}`}
                    onClick={() => onMarkRead(notif.id)}
                  >
                    <span className="mobile-notif-icon">
                      {notif.type === 'auction_won'     && '🏆'}
                      {notif.type === 'auction_sold'    && '💰'}
                      {notif.type === 'auction_expired' && '⏰'}
                      {notif.type === 'item_sold'       && '💰'}
                      {notif.type === 'daily_reward'    && '🎁'}
                      {notif.type === 'achievement'     && '⭐'}
                    </span>
                    <div className="mobile-notif-content">
                      <div className="mobile-notif-msg">{notif.message}</div>
                      <div className="mobile-notif-time">{formatTimeAgo(notif.timestamp)}</div>
                    </div>
                    {!notif.read && <span className="mobile-notif-dot" />}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <nav className="v2-mobile-bottom">
        {BOTTOM_TABS.map(t => {
          const active = page === t.id;
          return (
            <button
              key={t.id}
              type="button"
              className={`v2-mobile-tab${active ? ' v2-mobile-tab--active' : ''}`}
              onClick={() => setPage(t.id)}
            >
              <t.Icon />
              <span className="v2-mobile-tab__label">{t.label}</span>
              {t.id === 'pass' && hasClaimablePassRewards && <span className="v2-mobile-tab__dot" />}
            </button>
          );
        })}
      </nav>
    </>
  );
}
